// Plays a ~10 second celebratory jingle using the Web Audio API (no audio files needed).
// Must be called from within a user gesture (e.g. a tap) so iOS Safari will unlock audio.

let audioCtx: AudioContext | null = null;

const getContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  return audioCtx;
};

// Note frequencies (Hz)
const N: Record<string, number> = {
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, B5: 987.77, C6: 1046.5,
  C3: 130.81, F3: 174.61, G3: 196.0, A3: 220.0,
};

const BEAT = 0.19; // seconds per eighth note (~158 BPM) -> ~10s total

// [note, length in eighths]; null = rest
type Step = [string | null, number];

// A bright, bouncy ~10s fanfare in C major
const MELODY: Step[] = [
  // Fanfare opening
  ['C5', 1], ['E5', 1], ['G5', 1], ['C6', 2], ['G5', 1], ['C6', 3],
  // Phrase 1
  ['E5', 1], ['G5', 1], ['A5', 1], ['G5', 1], ['E5', 2], ['C5', 2],
  ['D5', 1], ['E5', 1], ['F5', 1], ['E5', 1], ['D5', 2], ['G4', 2],
  // Phrase 2
  ['E5', 1], ['G5', 1], ['A5', 1], ['G5', 1], ['E5', 2], ['C5', 2],
  ['D5', 1], ['F5', 1], ['E5', 1], ['D5', 1], ['C5', 4],
  // Big finish
  ['G5', 1], ['A5', 1], ['B5', 1], ['C6', 1], [null, 1], ['C6', 1], [null, 1], ['C6', 4],
];

const BASS: Step[] = [
  ['C3', 4], ['C3', 4], ['C3', 1], ['G3', 1], ['C3', 2], ['A3', 4],
  ['F3', 4], ['G3', 4], ['C3', 4], ['A3', 4], ['F3', 4], ['G3', 4], ['C3', 8], ['C3', 4],
];

const playVoice = (
  ctx: AudioContext,
  dest: AudioNode,
  steps: Step[],
  type: OscillatorType,
  gainLevel: number,
  startAt: number
) => {
  let t = startAt;
  for (const [note, len] of steps) {
    const dur = len * BEAT;
    if (note) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = N[note];
      const attack = 0.01;
      const release = Math.min(0.12, dur * 0.4);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(gainLevel, t + attack);
      gain.gain.setValueAtTime(gainLevel, t + dur - release);
      gain.gain.linearRampToValueAtTime(0, t + dur);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    }
    t += dur;
  }
  return t;
};

export const playCelebration = (): void => {
  try {
    const ctx = getContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
    const master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);

    const start = ctx.currentTime + 0.05;
    playVoice(ctx, master, MELODY, 'triangle', 0.9, start);
    playVoice(ctx, master, BASS, 'sine', 0.6, start);
  } catch (err) {
    // Audio is a nice-to-have; never let it break the checklist
    console.warn('Celebration sound failed:', err);
  }
};

// Total duration in seconds (exported for tests / reference)
export const CELEBRATION_DURATION = MELODY.reduce((sum, [, len]) => sum + len, 0) * BEAT;
